import {resolvePartnerSlugOrLocal} from '../lib/partner-slug'
import {apiFetch, apiBaseUrl} from '@fitro/customer-host/api'

/** Once given, a gender can only be switched, not cleared. */
export type CustomerGender = 'FEMALE' | 'MALE'

export interface CustomerProfile {
    id: string | null
    customerId: string | null
    authUserId: string
    email: string
    primaryEmail: string
    firstName: string
    lastName: string
    phone: string
    facebookProfile: string
    instagramProfile: string
    dateOfBirth?: string | null
    gender?: CustomerGender | null
    studioMarketingConsent: boolean
    fitroMarketingConsent: boolean
    imageMarketingConsent: boolean
    serviceTermsAvailable: boolean
    serviceTermsAccepted: boolean
    serviceTermsAcceptedAt?: string | null
    status: string
}

export interface UpdateCustomerProfileRequest {
    firstName?: string
    lastName?: string
    phone?: string
    facebookProfile?: string
    instagramProfile?: string
    dateOfBirth?: string
    gender?: CustomerGender
    studioMarketingConsent?: boolean
    fitroMarketingConsent?: boolean
    imageMarketingConsent?: boolean
}

export interface UpdateCustomerPhoneRequest {
    phone: string
    studioMarketingConsent?: boolean
    fitroMarketingConsent?: boolean
    imageMarketingConsent?: boolean
}

export interface CustomerPhoneResponse {
    phone: string
    studioMarketingConsent: boolean
    fitroMarketingConsent: boolean
    imageMarketingConsent: boolean
}

export interface CustomerAvatarSyncResponse {
    avatarUrl: string | null
}

export type CustomerNotificationChannel = 'EMAIL' | 'SMS'

export interface CustomerNotificationPreference {
    eventType: string
    channel: CustomerNotificationChannel
    enabled: boolean
}

let linkInFlight: Promise<void> | null = null

export async function getCustomerProfile(): Promise<CustomerProfile> {
    return apiFetch<CustomerProfile>('/api/customer/profile')
}

export async function updateCustomerProfile(
    payload: UpdateCustomerProfileRequest
): Promise<CustomerProfile> {
    return apiFetch<CustomerProfile>('/api/customer/profile', {
        method: 'PUT',
        body: JSON.stringify(payload)
    })
}

export async function updateCustomerPhone(
    payload: UpdateCustomerPhoneRequest
): Promise<CustomerPhoneResponse> {
    return apiFetch<CustomerPhoneResponse>('/api/customer/profile/phone', {
        method: 'PUT',
        body: JSON.stringify(payload)
    })
}

export async function getCustomerNotifications(): Promise<CustomerNotificationPreference[]> {
    const response = await apiFetch<{items: CustomerNotificationPreference[]}>('/api/customer/profile/notifications')
    return response.items ?? []
}

export async function updateCustomerNotification(
    preference: CustomerNotificationPreference
): Promise<CustomerNotificationPreference> {
    return apiFetch<CustomerNotificationPreference>('/api/customer/profile/notifications', {
        method: 'PATCH',
        body: JSON.stringify(preference)
    })
}

export async function syncCustomerAvatar(): Promise<CustomerAvatarSyncResponse> {
    return apiFetch<CustomerAvatarSyncResponse>('/api/customer/profile/avatar/sync', {
        method: 'POST'
    })
}

export async function linkCustomerToPartner(): Promise<void> {
    if (linkInFlight) return linkInFlight

    linkInFlight = (async () => {
        const partnerSlug = resolvePartnerSlugOrLocal()
        const path = `/api/public/partners/${encodeURIComponent(partnerSlug)}/customers/link-me`
        try {
            await apiFetch<void>(path, {
                method: 'POST'
            })
        } catch (error) {
            if (isConflictStatus(error)) return
            throw error
        } finally {
            linkInFlight = null
        }
    })()

    return linkInFlight
}

export const customerProfileApiAvailable = Boolean(apiBaseUrl)

function isConflictStatus(error: unknown): boolean {
    if (!error || typeof error !== 'object') return false
    if (!('status' in error)) return false
    return Number((error as {status: unknown}).status) === 409
}
